import React, { useState } from "react";
import { FaCheck } from "react-icons/fa";

// Display-only — the actual charge amount is always computed server-side
// (create-checkout-session.php owns the real price and re-validates any
// promo code fresh before charging).
const BASE_PRICE_CENTS = 95000;

const formatCents = (cents) => `$${(cents / 100).toFixed(2)}`;

const FundingLabEnrollArea = () => {
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [applicationId, setApplicationId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, description, discountedAmountCents }
  const [promoError, setPromoError] = useState("");
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [isPromoFieldVisible, setIsPromoFieldVisible] = useState(false);

  const displayedPriceCents = appliedPromo ? appliedPromo.discountedAmountCents : BASE_PRICE_CENTS;

  const handleApplyPromo = async () => {
    const code = promoInput.trim();
    setPromoError("");
    if (!code) {
      setAppliedPromo(null);
      return;
    }

    setIsValidatingPromo(true);
    try {
      const response = await fetch(
        `/api/validate-promo-code.php?code=${encodeURIComponent(code)}&tier=full`
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.valid) {
        setAppliedPromo(null);
        setPromoError((data && data.error) || "This code isn't valid or has expired.");
        return;
      }

      // A malformed response (e.g. a stale/broken deploy of the validate
      // endpoint) must never silently check a seat out for free or render
      // a blank "()" label — treat anything outside a sane range as a
      // failure, not a $0 success.
      const discountedAmountCents = data.discounted_amount_cents;
      const description = data.description;
      const isValidAmount =
        Number.isInteger(discountedAmountCents) &&
        discountedAmountCents >= 0 &&
        discountedAmountCents <= BASE_PRICE_CENTS;
      const isValidDescription = typeof description === "string" && description.trim() !== "";
      if (!isValidAmount || !isValidDescription) {
        setAppliedPromo(null);
        setPromoError("Couldn't apply that code right now. Please try again or contact us.");
        return;
      }

      setAppliedPromo({
        code: data.code,
        description,
        discountedAmountCents,
      });
    } catch (err) {
      setAppliedPromo(null);
      setPromoError("Couldn't check that code right now. Please try again.");
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoInput("");
    setPromoError("");
    setIsPromoFieldVisible(false);
  };

  const handleEnroll = async () => {
    setError("");

    const trimmedEmail = email.trim();
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/create-checkout-session.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tier: "full",
          email: trimmedEmail,
          companyName: companyName.trim(),
          applicationId: applicationId.trim(),
          promoCode: appliedPromo ? appliedPromo.code : "",
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.url) {
        throw new Error(data.error || "Couldn't start checkout. Please try again.");
      }

      window.location.href = data.url;
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again or contact us.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="service-area pd-bottom-90 funding-lab-v2">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-7 col-12">
            <div className="fl2-section">
              <h1 className="fl2-section-title">Enroll in Funding Lab</h1>
              <p className="fl2-section-kicker">
                Reserve your seat in the next Funding Lab cohort — a 10-week, cohort-based program to
                build your capital stack, starting September 18.
              </p>

              <div className="fl2-enroll-price-card">
                <span className="fl2-enroll-price-label">Full Program</span>
                {appliedPromo ? (
                  <>
                    <span className="fl2-enroll-price-value">
                      CAD {formatCents(appliedPromo.discountedAmountCents)}
                      <span className="fl2-enroll-price-strike">CAD {formatCents(BASE_PRICE_CENTS)}</span>
                    </span>
                    <span className="fl2-enroll-price-note">
                      Promo "{appliedPromo.code}" applied ({appliedPromo.description}) · Cohort: Sept 18 – Nov 28
                    </span>
                  </>
                ) : (
                  <>
                    <span className="fl2-enroll-price-value">CAD {formatCents(BASE_PRICE_CENTS)}</span>
                    <span className="fl2-enroll-price-note">One-time payment · Cohort: Sept 18 – Nov 28</span>
                  </>
                )}
              </div>

              <div className="fl2-field">
                {appliedPromo ? (
                  <>
                    <label htmlFor="enroll-promo">Promo code</label>
                    <div className="fl2-promo-applied-row">
                      <span className="fl2-promo-applied-badge">
                        <FaCheck /> {appliedPromo.code} applied — {appliedPromo.description}
                      </span>
                      <button type="button" className="fl2-btn-text" onClick={handleRemovePromo}>
                        Remove
                      </button>
                    </div>
                  </>
                ) : isPromoFieldVisible ? (
                  <>
                    <label htmlFor="enroll-promo">Promo code</label>
                    <div className="fl2-promo-input-row">
                      <input
                        id="enroll-promo"
                        type="text"
                        value={promoInput}
                        placeholder="Enter a promo code"
                        autoFocus
                        onChange={(e) => setPromoInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleApplyPromo();
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="fl2-btn fl2-btn-solid"
                        onClick={handleApplyPromo}
                        disabled={isValidatingPromo || !promoInput.trim()}
                      >
                        {isValidatingPromo ? "Checking…" : "Apply"}
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    className="fl2-promo-toggle"
                    onClick={() => setIsPromoFieldVisible(true)}
                  >
                    Have a promo code?
                  </button>
                )}
                {promoError && (
                  <p className="fl2-field-error" role="alert">
                    {promoError}
                  </p>
                )}
              </div>

              <div className="fl2-field">
                <label htmlFor="enroll-email">Email</label>
                <input
                  id="enroll-email"
                  type="email"
                  value={email}
                  placeholder="you@company.com"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="fl2-field">
                <label htmlFor="enroll-company">Company name</label>
                <input
                  id="enroll-company"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </div>

              <div className="fl2-field">
                <label htmlFor="enroll-application-id">Name</label>
                <input
                  id="enroll-application-id"
                  type="text"
                  value={applicationId}
                  onChange={(e) => setApplicationId(e.target.value)}
                />
              </div>

              {error && (
                <p className="fl2-field-error" role="alert">
                  {error}
                </p>
              )}

              <button
                type="button"
                className="fl2-btn fl2-btn-solid fl2-btn-lg w-100 justify-content-center"
                onClick={handleEnroll}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Redirecting to checkout…"
                  : `Continue to Payment — CAD ${formatCents(displayedPriceCents)}`}
              </button>

              <p className="fl2-enroll-refund-note">
                Fully refundable until September 18, 2026 — the day the cohort begins. No refunds after
                the program starts.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundingLabEnrollArea;
